import React, { useEffect, useState } from 'react';

import {
  BLOOD_PRESSURE,
  BODY_FAT_PERCENTAGE,
  CARDIORESPIRATORY_FITNESS,
} from '@constants/fhir';
import referenceRanges from '@constants/reference-range/index';
import type { Range } from '@constants/reference-range/types';

import {
  AssessmentResultActions,
  AssessmentResultContext,
} from '@contexts/index';

import { BloodPressureRiskLevel } from '@models/assessment';
import { ObservationRecord } from '@models/observation';

import { displayRange } from '@utils/assessment';

import { Gender } from 'generated/types';

interface IResult {
  risk: string;
  observation?: ObservationRecord | null;
  value?: number;
}

export function useAssessmentResult() {
  const context = React.useContext(AssessmentResultContext);

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (context === undefined) {
    throw new Error(
      `useAssessmentResult must be used within a AssessmentResultProvider`
    );
  }

  const [resultsMap, setResultsMap] = useState<Record<string, IResult>>({});
  const { dispatch, state, loading, data } = context;
  const riskAssessment = data?.riskAssessment ?? null;

  React.useEffect(() => {
    if (!riskAssessment) {
      return;
    }
    for (const { code, risk, value } of riskAssessment.predictions ?? []) {
      if (code && risk) {
        setResultsMap((prev) => ({
          ...prev,
          [code]: {
            ...prev[code],
            risk,
            ...(typeof value === 'number' && {
              value,
            }),
          },
        }));
      }
    }
    for (const obs of riskAssessment.getObservations()) {
      const observation = new ObservationRecord(obs);
      const vitallCode = observation.code;
      setResultsMap((prev) => ({
        ...prev,
        [vitallCode]: {
          ...prev[vitallCode],
          observation,
        },
      }));
    }
    // `resultsMap` is deliberately NOT a dependency: every branch above calls
    // setResultsMap with a fresh object, so depending on it re-triggered this
    // effect on its own output and spun forever. The functional updaters below
    // already read the latest state, so keying on riskAssessment is sufficient.
  }, [riskAssessment]);

  const getResult = (code: string): IResult | undefined => {
    if (!(code in resultsMap)) {
      return undefined;
    }
    const result = resultsMap[code];
    const risk =
      typeof result.value === 'number' ||
      typeof result.observation?.value === 'number'
        ? (determineRiskLevel(
            code,
            typeof result.observation?.value === 'number'
              ? result.observation.value
              : 0
          ) ?? result.risk)
        : result.risk;

    switch (code) {
      case CARDIORESPIRATORY_FITNESS:
      case BODY_FAT_PERCENTAGE:
        return {
          ...result,
          risk,
        };
      default:
        return result;
    }
  };

  useEffect(() => {
    // Keyed on `data` alone: a pre-resolved result (the external share page) has
    // no assessment id to key on, and the reducer ignores the payload anyway.
    if (data) {
      dispatch({
        type: AssessmentResultActions.Initialize,
        id: state.id ?? '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, state.id]);

  const determineRiskLevel = (code: string, value: number) => {
    const sex = data?.profile?.sex ?? '';
    const sexReferenceRange = referenceRanges.get(code)?.get(sex);
    const generalReferenceRange = referenceRanges
      .get(code)
      ?.get(Gender.unknown);
    const referenceRange = sexReferenceRange ?? generalReferenceRange;
    if (!referenceRange) {
      return undefined;
    }

    function sortBpRisks<V>(
      arr: [BloodPressureRiskLevel, V[]][]
    ): [BloodPressureRiskLevel, V[]][] {
      const prioritySet = new Set<BloodPressureRiskLevel>([
        BloodPressureRiskLevel.Normal,
        BloodPressureRiskLevel.Elevated,
      ]);

      return arr.sort((a, b) => {
        const aPriority = prioritySet.has(a[0]) ? 0 : 1;
        const bPriority = prioritySet.has(b[0]) ? 0 : 1;
        return aPriority - bPriority;
      });
    }

    let referenceRangeEntries = Array.from(referenceRange.entries()).reverse();

    if (code === BLOOD_PRESSURE) {
      referenceRangeEntries = sortBpRisks(
        referenceRangeEntries as [BloodPressureRiskLevel, Range[]][]
      );
    }

    // Reverse reference range entries to match highest risk first
    for (const [riskLevel, components] of referenceRangeEntries) {
      const range = components.at(0);
      const { isInRange } = displayRange(value, range?.lower, range?.upper);
      if (isInRange) {
        return riskLevel;
      }
    }
  };

  return {
    ...state,
    loading: loading || !state.isInitialized,
    data: riskAssessment,
    getResult,
    profile: data?.profile ?? undefined,
  };
}
